import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { User } from '../app/models/user.model';
import { UserService } from './user-service.service';
import { UserProfileService } from './user-profile.service';
import { GuardianLinkService } from './guardian-link.service';
import { HybridStorageService, HybridStorageKeys } from '../app/services/hybrid-storage.service';
import { IdentitySwitchService } from './identity-switch.service';

/**
 * Shared staff <-> linked-parent ("parent") account switching, used by both the web header switcher
 * and the mobile footer menu so the two can never drift. Each switch does a full page reload — so
 * every component re-initializes cleanly as the new identity — landing on `returnUrl`, which the
 * caller passes as the current route to keep the user where they were instead of on Home.
 */
@Injectable({ providedIn: 'root' })
export class AccountSwitchService {
  constructor(private guardianLinkService: GuardianLinkService,
              private userProfileService: UserProfileService,
              private userService: UserService,
              private hybridStorageService: HybridStorageService,
              private identitySwitch: IdentitySwitchService) { }

  /** Switches a staff user into their linked guardian ("parent") account via impersonation. */
  switchToParent(returnUrl: string): void {
    this.guardianLinkService.switchToGuardian().subscribe({
      next: async (result: User) => {
        // This document is still the staff page until the reload commits, so keep its API calls
        // held past setCurrentUserProfile() (see IdentitySwitchService).
        this.identitySwitch.holdUntilReload();
        try {
          // Mirror the storage handling used when stopping impersonation.
          await this.hybridStorageService.clearStorageForImpersonation();
          await this.userService.setCurrentUserProfile(result);
          await this.hybridStorageService.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);
          sessionStorage.setItem('impersonation_session_active', 'true');
          // Marks this impersonation as a self-service parent-account switch so the header reads
          // "Parent Account" rather than "Impersonating".
          sessionStorage.setItem('parent_account_switch', 'true');
        } finally {
          // Always reload: the server has already switched, and the reload is the only thing that
          // lowers the hold, so skipping it on a storage failure would freeze the app.
          window.location.href = returnUrl;
        }
      },
      error: (err: HttpErrorResponse) => console.error('Error switching to parent account', err)
    });
  }

  /** Returns from a parent-account view (or any impersonation) to the real underlying user. */
  switchToStaff(returnUrl: string): void {
    this.userProfileService.clearImpersonation().subscribe({
      next: async (result: User) => {
        this.identitySwitch.holdUntilReload();
        try {
          await this.hybridStorageService.clearStorageForImpersonation();
          await this.userService.setCurrentUserProfile(result);
          await this.hybridStorageService.setItem(HybridStorageKeys.MainMenu_UserFirstName, this.userService.CurrentUserProfile?.FirstName);
        } finally {
          window.location.href = returnUrl;
        }
      },
      error: (err: HttpErrorResponse) => console.error('Error stopping impersonation', err)
    });
  }
}
